import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Home as HomeIcon, Sparkles, MapPin, CalendarDays, CreditCard, ArrowRight, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { scrollTo } from "@/components/Navbar";

const TRACKS = {
  investor: {
    icon: TrendingUp,
    title: "I'm investing",
    sub: "High yields, capital growth, off-plan entry",
    blurb: "Five upcoming launches chosen for rental yield, payment-plan leverage and resale upside.",
    points: [
      "Target 8–10% gross yields in hand-picked high-demand communities",
      "Payment-plan leverage — 60/40, 70/30 and 1%-monthly structures",
      "Exit and resale modelling before you commit a dirham",
      "Launch-day pricing and DLD fee waivers on selected projects",
    ],
  },
  end_user: {
    icon: HomeIcon,
    title: "I'm buying a home",
    sub: "Family communities, waterfront living, ready comfort",
    blurb: "Five communities chosen for schools, greenery, space and everyday quality of life.",
    points: [
      "Communities matched to schools, parks, commute and lifestyle",
      "Mortgage pre-approval strategy + First-Time Buyer Program perks",
      "Golden Visa eligibility check on AED 2M+ homes",
      "Hand-holding through Form F, NOC and the DLD title transfer",
    ],
  },
};

const ProjectCard = ({ p, large }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 28 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className={`gold-ring rounded-2xl overflow-hidden bg-ink-card group flex flex-col ${
      large ? "md:col-span-2 lg:col-span-2" : ""
    }`}
    data-testid={`project-card-${p.id}`}
  >
    <div className={`relative overflow-hidden ${large ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
      <img
        src={p.image_url}
        alt={p.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
      <span className="absolute top-4 left-4 glass rounded-full px-3.5 py-1.5 text-[11px] tracking-wide text-gold-pale flex items-center gap-1.5">
        <MapPin size={11} className="text-gold" /> {p.location}
      </span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-xl text-[#F8F6F0] leading-snug">{p.name}</h3>
        <span className="shrink-0 text-xs font-semibold text-gold bg-gold/10 border border-gold/25 rounded-full px-3 py-1">
          {p.highlight}
        </span>
      </div>
      <p className="mt-1 font-serif text-lg gold-text">{p.price}</p>
      <div className="mt-4 space-y-2 text-xs text-zinc-400">
        {p.handover && (
          <p className="flex items-center gap-2"><CalendarDays size={13} className="text-gold/80" /> Handover: {p.handover}</p>
        )}
        {p.payment_plan && (
          <p className="flex items-center gap-2"><CreditCard size={13} className="text-gold/80" /> {p.payment_plan}</p>
        )}
      </div>
      {p.why && <p className="mt-4 text-sm text-zinc-300 leading-relaxed flex-1">{p.why}</p>}
      <button
        data-testid={`project-enquire-btn-${p.id}`}
        onClick={() => scrollTo("booking")}
        className="mt-5 inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-gold-light hover:text-gold-pale transition-colors self-start"
      >
        Enquire about this project <ArrowRight size={14} />
      </button>
    </div>
  </motion.article>
);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const PdfModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);

  const startDownload = (t) => {
    const a = document.createElement("a");
    a.href = `${API}/guide/pdf?token=${t}`;
    a.download = "Haven-Hill-Dubai-Buyers-Guide-2026.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/guide-order", form);
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Could not load the secure payment window — check your connection and retry.");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Haven & Hill Properties",
        description: "Dubai Buyer's Mini Guide 2026 (PDF)",
        order_id: order.order_id,
        prefill: { name: form.name, email: form.email },
        theme: { color: "#D4AF37" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/payments/guide-verify", {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            setToken(data.token);
            startDownload(data.token);
            toast.success("Payment confirmed — your guide is downloading.");
          } catch {
            toast.error("Payment received but verification failed — email us your receipt and we'll send the guide.");
          } finally {
            setBusy(false);
          }
        },
      });
      rzp.open();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start the payment. Please try again.");
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-ink border border-white/10 px-4 py-3 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 transition-colors";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" data-testid="pdf-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-md gold-ring rounded-3xl bg-ink-card p-8"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl text-[#F8F6F0]">
            {token ? "Payment confirmed" : "Get the 2026 guide as a PDF"}
          </h3>
          <button onClick={onClose} data-testid="pdf-modal-close" className="text-zinc-500 hover:text-zinc-300 p-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {token ? (
          <div className="mt-6 text-center" data-testid="pdf-success">
            <p className="text-sm text-zinc-300 leading-relaxed">
              Thank you — your download has started. A receipt is on its way to your inbox.
            </p>
            <button
              onClick={() => startDownload(token)}
              data-testid="pdf-download-again-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors"
            >
              Download again <FileDown size={15} />
            </button>
          </div>
        ) : (
          <>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
              The full mini guide — both Top-5 project lists and the ten-step checklist — beautifully
              formatted to save, print and share. One secure payment of <span className="text-gold-pale font-semibold">AED 49</span> via Razorpay,
              instant download.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3.5">
              <input required placeholder="Full name" value={form.name} data-testid="pdf-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input required type="email" placeholder="Email" value={form.email} data-testid="pdf-email-input"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              <button type="submit" disabled={busy} data-testid="pdf-download-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink hover:bg-gold-hover disabled:opacity-60 transition-colors">
                {busy ? "Opening secure checkout…" : "Pay AED 49 & download"} <FileDown size={15} />
              </button>
              <p className="text-[10px] text-zinc-600 text-center">Cards, UPI and net-banking via Razorpay's secure checkout.</p>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const Guide = () => {
  const [track, setTrack] = useState("investor");
  const [projects, setProjects] = useState([]);
  const [guide, setGuide] = useState({ trends: [], offers: [] });
  const [loading, setLoading] = useState(true);
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => {
    api.get("/guide").then((r) => setGuide(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects?track=${track}`)
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [track]);

  return (
    <section id="guide" data-testid="guide-section" className="py-20 lg:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-6">
            The Dubai Buyer's Mini Guide · 2026
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F8F6F0] leading-tight">
            First, tell us <span className="italic gold-text">why you're buying.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
            Investors and home buyers win in different ways — so the guide adapts. Pick your track and
            we'll show the trends, offers and five upcoming projects that fit it.
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 gap-5 max-w-3xl">
          {Object.entries(TRACKS).map(([key, t]) => (
            <button
              key={key}
              data-testid={`category-${key === "investor" ? "investor" : "end-user"}-tab`}
              onClick={() => setTrack(key)}
              className={`text-left rounded-2xl p-6 border transition-all duration-300 ${
                track === key
                  ? "border-gold/60 bg-gold/10 shadow-[0_0_40px_rgba(212,175,55,0.08)]"
                  : "gold-ring bg-ink-card/60 hover:bg-ink-hover"
              }`}
            >
              <t.icon size={22} className={track === key ? "text-gold" : "text-zinc-500"} />
              <p className="mt-4 font-serif text-xl text-[#F8F6F0]">{t.title}</p>
              <p className="mt-1 text-xs text-zinc-400">{t.sub}</p>
              <ul className="mt-4 space-y-1.5">
                {t.points.slice(0, 2).map((pt) => (
                  <li key={pt} className="text-[11px] text-zinc-400 flex gap-2 leading-snug">
                    <span className={`w-1 h-1 rotate-45 mt-1.5 shrink-0 ${track === key ? "bg-gold" : "bg-zinc-600"}`} />
                    {pt}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={track}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-3xl gold-ring rounded-2xl bg-gold/[0.05] p-7"
            data-testid="track-benefits-panel"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-gold-light font-medium mb-4">
              Your {track === "investor" ? "investor" : "home-buyer"} track unlocks
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {TRACKS[track].points.map((pt) => (
                <li key={pt} className="text-sm text-zinc-200 flex gap-2.5 leading-snug">
                  <span className="w-1 h-1 rotate-45 bg-gold mt-2 shrink-0" />
                  {pt}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
              {track === "investor"
                ? "Everything below — trends, offers and the Top 5 — is now filtered for return on investment."
                : "Everything below — trends, offers and the Top 5 — is now filtered for everyday living."}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <div data-testid="guide-trends">
            <h3 className="font-serif text-2xl text-gold-pale mb-5 flex items-center gap-2">
              <TrendingUp size={18} className="text-gold" /> Latest market trends
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {guide.trends.map((t, i) => (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.6 }}
                  className="gold-ring rounded-xl p-5 bg-ink-card/60"
                >
                  <p className="font-serif text-base text-[#F8F6F0]">{t.title}</p>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{t.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div data-testid="guide-offers">
            <h3 className="font-serif text-2xl text-gold-pale mb-5 flex items-center gap-2">
              <Sparkles size={18} className="text-gold" /> Attractive offers right now
            </h3>
            <div className="space-y-4">
              {guide.offers.map((o, i) => (
                <motion.div
                  key={o.title}
                  initial={{ opacity: 0, x: 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.6 }}
                  className="rounded-xl p-5 bg-gradient-to-r from-gold/10 to-transparent border border-gold/20"
                >
                  <p className="font-serif text-base text-gold-pale">{o.title}</p>
                  <p className="mt-1.5 text-xs text-zinc-300 leading-relaxed">{o.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 gold-ring rounded-2xl bg-gradient-to-r from-gold/[0.12] via-ink-card to-ink-card p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          data-testid="guide-pdf-cta"
        >
          <span className="shrink-0 w-12 h-12 rounded-2xl bg-gold/15 border border-gold/30 flex items-center justify-center">
            <FileDown size={22} className="text-gold" />
          </span>
          <div className="flex-1">
            <h3 className="font-serif text-xl text-[#F8F6F0]">Take the whole guide with you</h3>
            <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed max-w-xl">
              The trends, the offers, both Top-5 lists and the ten-step checklist — one beautifully
              branded PDF to save, print or share. <span className="text-gold-pale">AED 49, instant download.</span>
            </p>
          </div>
          <button
            data-testid="guide-pdf-open-btn"
            onClick={() => setPdfOpen(true)}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors"
          >
            Get the PDF guide — AED 49
          </button>
        </motion.div>

        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <h3 className="font-serif text-2xl sm:text-3xl text-[#F8F6F0]" data-testid="top5-heading">
              Top 5 upcoming projects — <span className="italic gold-text">{track === "investor" ? "Investor track" : "Home-buyer track"}</span>
            </h3>
            <p className="text-xs text-zinc-500 max-w-sm">{TRACKS[track].blurb}</p>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-ink-card/60 animate-pulse aspect-[4/5]" />
              ))}
            </div>
          ) : (
            <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              <AnimatePresence mode="popLayout">
                {projects.map((p, i) => (
                  <ProjectCard key={p.id} p={p} large={i === 0} />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
          <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed max-w-3xl">
            Curated sample selections for 2026 based on public DLD/RERA market information. Availability,
            prices and payment plans change at launch — always verify the project and escrow account on the
            Dubai REST app before committing.
          </p>
        </div>
      </div>
      {pdfOpen && <PdfModal onClose={() => setPdfOpen(false)} />}
    </section>
  );
};
